import globalAgent from 'src/agent';

import { readKnowledgeDocumentList } from './knowledgeDocumentList';

import { normalizeConversationContext } from './conversationContext';
import { canHandoff, ConversationHandoffError, isHumanControlled } from './conversationHandoff';

import { normalizeConversationTimeline } from './conversationTimeline';

import type { ConversationContext } from './conversationContext';

import type { ConversationTimelineItem } from './conversationTimeline';

export type { ConversationTimelineItem } from './conversationTimeline';

export type AIManagerStatus = 'draft' | 'needs_telegram' | 'ready_to_launch' | 'running' | 'paused' | 'error';

export type AIManagerTelegramStatus = 'connected' | 'not_connected' | 'warning';

export type AIManagerGoal = 'qualify' | 'recommend_service';

export type AIManagerAvatarPaletteId = 'aurora' | 'telegram' | 'violet' | 'mint';

export type AIManagerSetupStep = 'name' | 'knowledge' | 'scenario' | 'test';

export type AIManagerQualificationField = {
    id: string;
    label: string;
    question: string;
    required: boolean;
};

export type AIManagerFollowUpRule = {
    id: string;
    reason: string;
    delayHours: number;
    message: string;
};

export type AIManagerKnowledgeContacts = {
    phones: string[];
    emails: string[];
    links: string[];
};

export type AIManagerKnowledgeTypedItem = {
    type: string;
    label: string;
    value: string;
    sourceUrl?: string;
    sourceSnippet?: string;
    confidence?: number;
    reason?: string;
    isUseful?: boolean;
};

export type AIManagerKnowledgeSectionKey =
    | 'offer'
    | 'audience'
    | 'value'
    | 'process'
    | 'prices'
    | 'constraints'
    | 'contacts';

export type AIManagerDisplayKnowledgeItem = {
    id: string;
    text: string;
    origin: 'website' | 'manual' | 'fallback';
    sourceUrl?: string;
    sourceSnippet?: string;
    confidence?: number;
    type?: string;
};

export type AIManagerDisplayKnowledgeSection = {
    key: AIManagerKnowledgeSectionKey;
    title: string;
    items: AIManagerDisplayKnowledgeItem[];
};

export type AIManagerDisplayKnowledgeProfile = {
    summary: string;
    sections: AIManagerDisplayKnowledgeSection[];
    missingSections: AIManagerKnowledgeSectionKey[];
    warnings: string[];
};

export type AIManagerKnowledgeFacts = {
    usp: string[];
    clients: string[];
    services: string[];
    prices: string[];
    artifacts: string[];
    geography: string[];
    constraints: string[];
    sourceNotes: string[];
    contacts: AIManagerKnowledgeContacts;
    typedItems: AIManagerKnowledgeTypedItem[];
};

export type AIManagerKnowledgePage = {
    url: string;
    title: string;
};

export type AIManagerKnowledgeAnalysis = {
    sourceUrl: string;
    summary: string;
    facts: AIManagerKnowledgeFacts;
    pages: AIManagerKnowledgePage[];
    displayProfile: AIManagerDisplayKnowledgeProfile;
};

export type AIManagerKnowledgeDocument = {
    id: number;
    agentProfile: string;
    title: string;
    summary: string;
    body: string;
    scope: string;
    documentType: string;
    priority: number;
    sourceUrl: string;
    tags: string[];
    metadata: Record<string, unknown>;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
};

export type AIManagerInstructionTemplate = {
    id: number;
    slug: string;
    name: string;
    description: string;
    dataCollectionSchema: unknown[];
    metadata: Record<string, unknown>;
};

export type AIManagerLeadStream = {
    id: number;
    name: string;
    displayName?: string;
    status?: string;
    statusLabel?: string;
    category?: {
        id?: number | null;
        name?: string;
        icon?: string;
        color?: string;
    };
};

export type AIManagerStats = {
    total: number;
    active: number;
    waitingReview: number;
    paymentPending: number;
    completed: number;
};

export type AIManager = {
    id: string;
    name: string;
    role: string;
    product: string;
    audience: string;
    leadStreamIds: number[];
    leadStreamNames: string[];
    leadStreams: AIManagerLeadStream[];
    goal: AIManagerGoal;
    goalDescription: string;
    flowSteps: string[];
    dataToCollect: string[];
    openingMessages: string[];
    stopPhrases: string[];
    avatarPaletteId: AIManagerAvatarPaletteId;
    status: AIManagerStatus;
    statusLabel: string;
    telegramStatus: AIManagerTelegramStatus;
    dialogBalance: number;
    activeDialogs: number;
    reviewCount: number;
    wonDeals: number;
    readiness: number;
    goodLeadRules: string[];
    stopRules: string[];
    nextStep: string;
    handoffRule: string;
    firstMessage: string;
    offerNotes: string;
    knowledgeSource: string;
    knowledgeSourceUrl: string;
    knowledgeSummary: string;
    knowledgeFacts: AIManagerKnowledgeFacts | null;
    knowledgeDisplayProfile: AIManagerDisplayKnowledgeProfile | null;
    knowledgePages: AIManagerKnowledgePage[];
    qualificationFields: AIManagerQualificationField[];
    recommendationTarget: string;
    recommendationContext: string;
    followUps: AIManagerFollowUpRule[];
    stats: AIManagerStats;
    hasLaunched: boolean;
    llmModel: string;
    llmTemperature: number;
    llmTopP: number;
    llmMaxOutputTokens: number;
    maxMessagesPerDialog: number;
    lastStatusError?: string;
    metadata: Record<string, unknown>;
    createdAt: string;
    updatedAt: string;
};

export type NewAIManagerDraft = {
    name: string;
    leadStreamIds: number[];
    leadStreamNames: string[];
    goal: AIManagerGoal;
    goalDescription: string;
    flowSteps: string[];
    dataToCollect: string[];
    openingMessages: string[];
    handoffRule: string;
    stopPhrases: string[];
    avatarPaletteId: AIManagerAvatarPaletteId;
    firstMessage?: string;
    offerNotes?: string;
    knowledgeSource?: string;
    knowledgeSourceUrl?: string;
    knowledgeSummary?: string;
    knowledgeFacts?: AIManagerKnowledgeFacts | null;
    knowledgeDisplayProfile?: AIManagerDisplayKnowledgeProfile | null;
    knowledgePages?: AIManagerKnowledgePage[];
    qualificationFields?: AIManagerQualificationField[];
    recommendationTarget?: string;
    recommendationContext?: string;
    followUps?: AIManagerFollowUpRule[];
    product?: string;
    audience?: string;
    goodLeadRules?: string[];
    stopRules?: string[];
    nextStep?: string;
    tone?: string;
    llmModel?: string;
    llmTemperature?: number;
    llmTopP?: number;
    llmMaxOutputTokens?: number;
    maxMessagesPerDialog?: number;
    metadata?: Record<string, unknown>;
    status?: AIManagerStatus;
};

export type AIManagerFunnelStageKey =
    | 'new_contact'
    | 'interest_present'
    | 'target_lead'
    | 'decision_making'
    | 'payment'
    | 'closed'
    | 'rejected';

export type AIManagerConversation = {
    id: number;
    operatorOverrideActive?: boolean | null;
    status: string;
    salesStage: AIManagerFunnelStageKey;
    salesStageTitle: string;
    sourceMessage: string;
    sourceChatTitle?: string;
    sourceAuthor?: string;
    latestMessagePreview: string;
    latestOutboundStatus?: string | null;
    messageCount: number;
    outboundCount: number;
    reviewPriority?: string | number | null;
    updatedAt: string;
    createdAt: string;
    leadInfo?: {
        id?: number;
        category?: string;
        location?: string;
        chatName?: string;
        author?: string;
        messageDate?: string;
    };
};

export type AIManagerKanbanColumn = {
    key: AIManagerFunnelStageKey;
    title: string;
    count: number;
    items: AIManagerConversation[];
};

export type AIManagerConversationTranscript = {
    conversation: AIManagerConversation;
    timeline: ConversationTimelineItem[];
    context?: ConversationContext;
};

export type AIManagerHandoffResult = Pick<AIManagerConversationTranscript, 'conversation' | 'context'>;

const pendingHandoffs = new Map<string, Promise<AIManagerHandoffResult>>();
const uncertainHandoffs = new Set<string>();

export type AIManagerKanban = {
    agentProfile: string;
    agentProfileStatus?: AIManagerStatus;
    total: number;
    columns: AIManagerKanbanColumn[];
};

export type AIManagerTestDialogMessage = {
    id?: string;
    role: 'user' | 'assistant';
    content: string;
};

export type AIManagerTestDialogSessionState = {
    currentStageKey?: string | null;
    lifecycleStatus?: string | null;
    salesStage?: string | null;
    salesOutcome?: string | null;
    knownFacts: Record<string, unknown>;
};

export type AIManagerFieldProgress = {
    fields: Array<{
        id: string;
        label: string;
        required: boolean;
        collected: boolean;
        missing: boolean;
        value?: unknown;
    }>;
    requiredCount: number;
    collectedRequiredCount: number;
    complete: boolean;
};

export type AIManagerTestDialogResult = {
    assistantMessage: AIManagerTestDialogMessage;
    replyDraft: string;
    sessionId: number | null;
    sessionState: AIManagerTestDialogSessionState;
    reasoningSummary: string;
    missingFacts: string[];
    fieldProgress: AIManagerFieldProgress | null;
    suggestedSalesStage: string;
    usage?: {
        inputTokens: number;
        outputTokens: number;
    };
};

export type AIManagerTelegramAuthSession = {
    id: number;
    sessionToken: string;
    phoneNumber: string;
    status: 'code_requested' | 'password_required' | 'completed' | 'failed' | 'expired';
    lastError: string;
};

export const AI_MANAGER_GOAL_LABELS: Record<AIManagerGoal, string> = {
    qualify: 'Квалифицировать потенциального клиента',
    recommend_service: 'Рассказать про сайт или канал',
};

type BackendAIManager = {
    id: number | string;
    name?: string;
    description?: string;
    is_active?: boolean;
    status?: AIManagerStatus;
    status_label?: string;
    last_status_error?: string;
    lead_stream_ids?: number[];
    lead_streams?: Array<{
        id: number;
        name?: string;
        display_name?: string;
        status?: string;
        status_label?: string;
        category?: {
            id?: number | null;
            name?: string;
            icon?: string;
            color?: string;
        };
    }>;
    goal?: AIManagerGoal;
    goal_description?: string;
    flow_steps?: string[];
    data_to_collect?: string[];
    opening_message?: string;
    opening_messages?: string[];
    handoff_rule?: string;
    stop_phrases?: string[];
    avatar_palette_id?: AIManagerAvatarPaletteId;
    metadata?: Record<string, unknown>;
    llm_model?: string;
    llm_temperature?: number | string;
    llm_top_p?: number | string;
    llm_max_output_tokens?: number | string;
    max_messages_per_dialog?: number;
    no_reply_followup_enabled?: boolean;
    no_reply_followup_delay_hours?: number;
    no_reply_followup_text?: string;
    telegram_status?: AIManagerTelegramStatus;
    has_launched?: boolean;
    readiness?: number;
    next_step?: string;
    stats?: {
        total?: number;
        active?: number;
        waiting_review?: number;
        payment_pending?: number;
        completed?: number;
    };
    created_at?: string;
    updated_at?: string;
};

type BackendConversation = {
    id: number;
    operator_override?: { active?: unknown } | null;
    status?: string;
    sales_stage?: AIManagerFunnelStageKey;
    sales_stage_title?: string;
    source_message_snapshot?: string;
    source_chat_title_snapshot?: string;
    source_author_snapshot?: string;
    latest_message_preview?: string;
    latest_outbound_status?: string | null;
    message_count?: number;
    outbound_count?: number;
    review_priority?: string | number | null;
    updated_at?: string;
    created_at?: string;
    lead_info?: {
        id?: number;
        category?: string;
        location?: string;
        chat_name?: string;
        author?: string;
        message_date?: string;
    };
};

type BackendTestDialogResponse = {
    assistant_message?: {
        id?: string;
        role?: string;
        content?: string;
    };
    reply_draft?: string;
    session?: {
        id?: number;
    };
    session_state?: {
        current_stage_key?: string | null;
        lifecycle_status?: string | null;
        sales_stage?: string | null;
        sales_outcome?: string | null;
        known_facts?: Record<string, unknown>;
    };
    reasoning_summary?: string;
    missing_facts?: string[];
    field_progress?: unknown;
    suggested_sales_stage?: string;
    usage?: {
        input_tokens?: number;
        output_tokens?: number;
    };
};

type BackendWebsiteKnowledgeAnalysis = {
    source_url?: string;
    sourceUrl?: string;
    summary?: string;
    facts?: unknown;
    pages?: unknown;
    display_profile?: unknown;
    displayProfile?: unknown;
};

type BackendKnowledgeDocument = {
    id: number;
    agent_profile?: number | string;
    title?: string;
    summary?: string;
    body?: string;
    scope?: string;
    document_type?: string;
    priority?: number;
    source_url?: string | null;
    tags?: unknown;
    metadata?: Record<string, unknown> | null;
    is_active?: boolean;
    created_at?: string;
    updated_at?: string;
};

type BackendInstructionTemplate = {
    id: number;
    slug?: string;
    name?: string;
    description?: string;
    data_collection_schema?: unknown[];
    metadata?: Record<string, unknown> | null;
};

type BackendTelegramAuthSession = {
    id: number;
    session_token?: string;
    phone_number?: string;
    status?: AIManagerTelegramAuthSession['status'];
    last_error?: string;
};

function normalizeRules(values?: unknown): string[] {
    return Array.isArray(values) ? values.map((value) => String(value || '').trim()).filter(Boolean) : [];
}

function createEmptyKnowledgeFacts(): AIManagerKnowledgeFacts {
    return {
        usp: [],
        clients: [],
        services: [],
        prices: [],
        artifacts: [],
        geography: [],
        constraints: [],
        sourceNotes: [],
        contacts: {
            phones: [],
            emails: [],
            links: [],
        },
        typedItems: [],
    };
}

function normalizeKnowledgeFacts(value?: unknown): AIManagerKnowledgeFacts | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const source = value as Record<string, unknown>;
    const contacts =
        source.contacts && typeof source.contacts === 'object' ? (source.contacts as Record<string, unknown>) : {};

    return {
        usp: normalizeRules(source.usp),
        clients: normalizeRules(source.clients),
        services: normalizeRules(source.services),
        prices: normalizeRules(source.prices),
        artifacts: normalizeRules(source.artifacts),
        geography: normalizeRules(source.geography),
        constraints: normalizeRules(source.constraints),
        sourceNotes: normalizeRules(source.source_notes || source.sourceNotes),
        contacts: {
            phones: normalizeRules(contacts.phones),
            emails: normalizeRules(contacts.emails),
            links: normalizeRules(contacts.links),
        },
        typedItems: normalizeKnowledgeTypedItems(source.typed_items || source.typedItems),
    };
}

function normalizeKnowledgePages(value?: unknown): AIManagerKnowledgePage[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((page) => {
            const source = page && typeof page === 'object' ? (page as Record<string, unknown>) : {};
            return {
                url: String(source.url || '').trim(),
                title: String(source.title || '').trim(),
            };
        })
        .filter((page) => page.url);
}

function normalizeKnowledgeTypedItems(value?: unknown): AIManagerKnowledgeTypedItem[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((item) => {
            const source = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
            const type = String(source.type || '').trim();
            const valueText = String(source.value || '').trim();

            if (!type || !valueText) {
                return null;
            }

            const rawConfidence = source.confidence;
            const confidenceValue =
                typeof rawConfidence === 'number'
                    ? rawConfidence
                    : typeof rawConfidence === 'string' && rawConfidence.trim()
                    ? Number(rawConfidence)
                    : undefined;
            const hasConfidence = typeof confidenceValue === 'number' && Number.isFinite(confidenceValue);
            const rawIsUseful = source.is_useful ?? source.isUseful;

            return {
                type,
                label: String(source.label || type).trim(),
                value: valueText,
                sourceUrl: String(source.source_url || source.sourceUrl || '').trim() || undefined,
                sourceSnippet: String(source.source_snippet || source.sourceSnippet || '').trim() || undefined,
                confidence: hasConfidence ? Math.max(0, Math.min(1, confidenceValue)) : undefined,
                reason: String(source.reason || '').trim() || undefined,
                isUseful: typeof rawIsUseful === 'boolean' ? rawIsUseful : undefined,
            };
        })
        .filter(Boolean) as AIManagerKnowledgeTypedItem[];
}

const KNOWLEDGE_SECTION_TITLES: Record<AIManagerKnowledgeSectionKey, string> = {
    offer: 'Что вы предлагаете',
    audience: 'Кому подходит',
    value: 'Почему выбирают',
    process: 'Как работает',
    prices: 'Цены и условия оплаты',
    constraints: 'Ограничения',
    contacts: 'Контакты',
};

const KNOWLEDGE_SECTION_ORDER = Object.keys(KNOWLEDGE_SECTION_TITLES) as AIManagerKnowledgeSectionKey[];

const KNOWLEDGE_TYPE_TO_SECTION: Record<string, AIManagerKnowledgeSectionKey> = {
    service: 'offer',
    client: 'audience',
    geography: 'audience',
    usp: 'value',
    artifact: 'value',
    price: 'prices',
    constraint: 'constraints',
    contact: 'contacts',
};

function createDisplayItemId(sectionKey: string, text: string, index: number) {
    let hash = 0;
    const source = `${sectionKey}|${text}`;
    for (let i = 0; i < source.length; i += 1) {
        hash = (hash * 31 + source.charCodeAt(i)) >>> 0;
    }
    return `${sectionKey}-${hash.toString(16)}-${index}`;
}

function looksLikeProcessItem(value: string) {
    return /как работает|запуск|уведомлен|анализ|подбор|фильтр|монитор|этап|интеграц|автомат/i.test(value);
}

function looksLikeConstraintItem(value: string) {
    return /не работаем|минималь|огранич|оплат|договор|срок|чек|гарант|нельзя|только/i.test(value);
}

export function normalizeKnowledgeDisplayProfile(
    value?: unknown,
    facts?: AIManagerKnowledgeFacts
): AIManagerDisplayKnowledgeProfile {
    const emptySections = KNOWLEDGE_SECTION_ORDER.map((key) => ({
        key,
        title: KNOWLEDGE_SECTION_TITLES[key],
        items: [],
    }));

    if (value && typeof value === 'object') {
        const source = value as Record<string, unknown>;
        const sections = Array.isArray(source.sections)
            ? source.sections
                  .map((section) => {
                      const rawSection =
                          section && typeof section === 'object' ? (section as Record<string, unknown>) : {};
                      const key = String(rawSection.key || '').trim() as AIManagerKnowledgeSectionKey;
                      if (!KNOWLEDGE_SECTION_TITLES[key]) {
                          return null;
                      }
                      const items = Array.isArray(rawSection.items)
                          ? rawSection.items
                                .map((item, index) => {
                                    const rawItem =
                                        item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
                                    const text = String(rawItem.text || '').trim();
                                    if (!text) {
                                        return null;
                                    }
                                    const confidence = Number(rawItem.confidence);
                                    return {
                                        id: String(rawItem.id || createDisplayItemId(key, text, index)),
                                        text,
                                        origin:
                                            rawItem.origin === 'manual' || rawItem.origin === 'fallback'
                                                ? rawItem.origin
                                                : 'website',
                                        sourceUrl:
                                            String(rawItem.source_url || rawItem.sourceUrl || '').trim() || undefined,
                                        sourceSnippet:
                                            String(rawItem.source_snippet || rawItem.sourceSnippet || '').trim() ||
                                            undefined,
                                        confidence: Number.isFinite(confidence)
                                            ? Math.max(0, Math.min(1, confidence))
                                            : undefined,
                                        type: String(rawItem.type || '').trim() || undefined,
                                    };
                                })
                                .filter(Boolean)
                          : [];
                      return {
                          key,
                          title: String(rawSection.title || KNOWLEDGE_SECTION_TITLES[key]).trim(),
                          items: items as AIManagerDisplayKnowledgeItem[],
                      };
                  })
                  .filter(Boolean)
            : [];
        const mergedSections = KNOWLEDGE_SECTION_ORDER.map(
            (key) =>
                (sections as AIManagerDisplayKnowledgeSection[]).find((section) => section.key === key) || {
                    key,
                    title: KNOWLEDGE_SECTION_TITLES[key],
                    items: [],
                }
        );
        return {
            summary: String(source.summary || '').trim(),
            sections: mergedSections,
            missingSections: normalizeRules(source.missing_sections || source.missingSections)
                .filter((key) => KNOWLEDGE_SECTION_TITLES[key as AIManagerKnowledgeSectionKey])
                .map((key) => key as AIManagerKnowledgeSectionKey),
            warnings: normalizeRules(source.warnings),
        };
    }

    const grouped = KNOWLEDGE_SECTION_ORDER.reduce((acc, key) => {
        acc[key] = [];
        return acc;
    }, {} as Record<AIManagerKnowledgeSectionKey, AIManagerDisplayKnowledgeItem[]>);
    const seen = KNOWLEDGE_SECTION_ORDER.reduce((acc, key) => {
        acc[key] = new Set<string>();
        return acc;
    }, {} as Record<AIManagerKnowledgeSectionKey, Set<string>>);

    (facts?.typedItems || []).forEach((item, index) => {
        let sectionKey = KNOWLEDGE_TYPE_TO_SECTION[item.type];
        if (item.type === 'service' && looksLikeProcessItem(item.value)) {
            sectionKey = 'process';
        }
        if (item.type === 'source_note') {
            sectionKey = looksLikeConstraintItem(item.value) ? 'constraints' : 'offer';
        }
        const text = String(item.value || '').trim();
        if (!sectionKey || !text || seen[sectionKey].has(text.toLowerCase())) {
            return;
        }
        seen[sectionKey].add(text.toLowerCase());
        grouped[sectionKey].push({
            id: createDisplayItemId(sectionKey, text, index),
            text,
            origin: item.type === 'source_note' ? 'manual' : 'website',
            sourceUrl: item.sourceUrl,
            sourceSnippet: item.sourceSnippet,
            confidence: item.confidence,
            type: item.type,
        });
    });

    const sections = emptySections.map((section) => ({
        ...section,
        items: grouped[section.key],
    }));
    const missingSections = sections.filter((section) => !section.items.length).map((section) => section.key);
    return {
        summary: sections
            .flatMap((section) => section.items.map((item) => `${section.title}: ${item.text}`))
            .join('\n'),
        sections,
        missingSections,
        warnings: [
            ...(missingSections.includes('prices') ? ['На открытых страницах не нашли явные цены'] : []),
            ...(missingSections.includes('contacts') ? ['Контакты не найдены автоматически'] : []),
        ],
    };
}

function normalizeKnowledgeAnalysis(data: BackendWebsiteKnowledgeAnalysis): AIManagerKnowledgeAnalysis {
    const facts = normalizeKnowledgeFacts(data.facts) || createEmptyKnowledgeFacts();
    return {
        sourceUrl: String(data.source_url || data.sourceUrl || '').trim(),
        summary: String(data.summary || '').trim(),
        facts,
        pages: normalizeKnowledgePages(data.pages),
        displayProfile: normalizeKnowledgeDisplayProfile(data.display_profile || data.displayProfile, facts),
    };
}

function normalizeBackendSourceUrl(value?: string | null) {
    const source = String(value || '').trim();
    if (!source) {
        return null;
    }
    return /^https?:\/\//i.test(source) ? source : `https://${source}`;
}

function normalizeGoal(value?: string): AIManagerGoal {
    if (value && value in AI_MANAGER_GOAL_LABELS) {
        return value as AIManagerGoal;
    }

    return 'qualify';
}

function normalizeStatus(value?: string, isActive?: boolean): AIManagerStatus {
    if (
        value === 'draft' ||
        value === 'needs_telegram' ||
        value === 'ready_to_launch' ||
        value === 'running' ||
        value === 'paused' ||
        value === 'error'
    ) {
        return value;
    }

    return isActive ? 'running' : 'draft';
}

function normalizeTelegramStatus(value?: string): AIManagerTelegramStatus {
    if (value === 'connected' || value === 'warning') {
        return value;
    }

    return 'not_connected';
}

function normalizeAvatarPalette(value?: string): AIManagerAvatarPaletteId {
    if (value === 'telegram' || value === 'violet' || value === 'mint') {
        return value;
    }

    return 'aurora';
}

function normalizeStats(stats?: BackendAIManager['stats']): AIManagerStats {
    return {
        total: Number(stats?.total || 0),
        active: Number(stats?.active || 0),
        waitingReview: Number(stats?.waiting_review || 0),
        paymentPending: Number(stats?.payment_pending || 0),
        completed: Number(stats?.completed || 0),
    };
}

function normalizeQualificationFields(values?: unknown): AIManagerQualificationField[] {
    if (!Array.isArray(values)) {
        return [];
    }

    return values
        .map((value, index) => {
            if (typeof value === 'string') {
                const label = value.trim();

                return label
                    ? {
                          id: `field-${index}`,
                          label,
                          question: '',
                          required: true,
                      }
                    : null;
            }

            if (!value || typeof value !== 'object') {
                return null;
            }

            const raw = value as Partial<AIManagerQualificationField>;
            const label = String(raw.label || '').trim();

            if (!label) {
                return null;
            }

            return {
                id: String(raw.id || `field-${index}`),
                label,
                question: String(raw.question || '').trim(),
                required: raw.required !== false,
            };
        })
        .filter(Boolean) as AIManagerQualificationField[];
}

function normalizeFollowUps(values?: unknown): AIManagerFollowUpRule[] {
    if (!Array.isArray(values)) {
        return [];
    }

    return values
        .map((value, index) => {
            if (!value || typeof value !== 'object') {
                return null;
            }

            const raw = value as Partial<AIManagerFollowUpRule>;
            const reason = String(raw.reason || '').trim();
            const message = String(raw.message || '').trim();
            const delayHours = Number(raw.delayHours || 24);

            if (!reason && !message) {
                return null;
            }

            return {
                id: String(raw.id || `follow-up-${index}`),
                reason,
                delayHours: Number.isFinite(delayHours) ? Math.min(168, Math.max(1, delayHours)) : 24,
                message,
            };
        })
        .filter(Boolean) as AIManagerFollowUpRule[];
}

function normalizeBackendFollowUps(
    manager: BackendAIManager,
    metadata: Record<string, unknown>
): AIManagerFollowUpRule[] {
    const metadataFollowUps = normalizeFollowUps(metadata.follow_up_rules || metadata.followUps);

    if (metadataFollowUps.length) {
        return metadataFollowUps;
    }

    if (!manager.no_reply_followup_enabled || !manager.no_reply_followup_text) {
        return [];
    }

    return [
        {
            id: 'no-reply',
            reason: 'Потенциальный клиент не ответил',
            delayHours: Number(manager.no_reply_followup_delay_hours || 24),
            message: manager.no_reply_followup_text,
        },
    ];
}

function buildFirstMessage(draft: NewAIManagerDraft) {
    const manualMessage = normalizeRules(draft.openingMessages)[0];

    if (manualMessage) {
        return manualMessage;
    }

    const messagesByGoal: Record<AIManagerGoal, string> = {
        qualify: 'Привет! Видел ваш запрос. Могу задать пару вопросов, чтобы понять задачу, сроки и бюджет.',
        recommend_service:
            'Привет! Видел ваш запрос. Могу подсказать подходящий вариант и коротко объяснить, почему он может подойти.',
    };

    return messagesByGoal[draft.goal];
}

function normalizeManager(manager: BackendAIManager): AIManager {
    const goal = normalizeGoal(manager.goal);
    const leadStreams = (manager.lead_streams || []).map((stream) => ({
        id: stream.id,
        name: stream.name || stream.category?.name || 'Поток потенциальных клиентов',
        displayName: stream.display_name,
        status: stream.status,
        statusLabel: stream.status_label,
        category: stream.category,
    }));
    const leadStreamIds = Array.isArray(manager.lead_stream_ids)
        ? manager.lead_stream_ids.map(Number).filter((value) => Number.isFinite(value))
        : leadStreams.map((stream) => stream.id);
    const leadStreamNames = leadStreams.map((stream) => {
        if (stream.displayName) {
            return stream.displayName;
        }

        const icon = stream.category?.icon || '';
        const name = stream.name || stream.category?.name || 'Поток потенциальных клиентов';
        return icon && !name.startsWith(icon) ? `${icon} ${name}` : name;
    });
    const firstMessage = manager.opening_message || normalizeRules(manager.opening_messages)[0] || '';
    const stats = normalizeStats(manager.stats);
    const status = normalizeStatus(manager.status, manager.is_active);
    const metadata = manager.metadata || {};
    const qualificationFields = normalizeQualificationFields(
        metadata.qualification_fields || metadata.qualificationFields || manager.data_to_collect
    );
    const followUps = normalizeBackendFollowUps(manager, metadata);

    return {
        id: String(manager.id),
        name: manager.name || 'AI-менеджер',
        role: AI_MANAGER_GOAL_LABELS[goal],
        product: leadStreamNames.join(', '),
        audience: 'Потенциальные клиенты из выбранных потоков',
        leadStreamIds,
        leadStreamNames,
        leadStreams,
        goal,
        goalDescription: manager.goal_description || manager.description || AI_MANAGER_GOAL_LABELS[goal],
        flowSteps: normalizeRules(manager.flow_steps),
        dataToCollect: normalizeRules(manager.data_to_collect),
        openingMessages: firstMessage ? [firstMessage] : [],
        stopPhrases: normalizeRules(manager.stop_phrases),
        avatarPaletteId: normalizeAvatarPalette(manager.avatar_palette_id),
        status,
        statusLabel: manager.status_label || status,
        telegramStatus: normalizeTelegramStatus(manager.telegram_status),
        dialogBalance: 0,
        activeDialogs: stats.active,
        reviewCount: stats.waitingReview,
        wonDeals: stats.completed,
        readiness: Number(manager.readiness || 0),
        goodLeadRules: [],
        stopRules: normalizeRules(manager.stop_phrases),
        nextStep: manager.next_step || '',
        handoffRule: manager.handoff_rule || '',
        firstMessage,
        offerNotes: String(metadata.offer_notes || metadata.offerNotes || ''),
        knowledgeSource: String(metadata.knowledge_source || metadata.knowledgeSource || ''),
        knowledgeSourceUrl: String(metadata.knowledge_source_url || metadata.knowledgeSourceUrl || ''),
        knowledgeSummary: String(metadata.knowledge_summary || metadata.knowledgeSummary || ''),
        knowledgeFacts: normalizeKnowledgeFacts(metadata.knowledge_facts || metadata.knowledgeFacts),
        knowledgeDisplayProfile: normalizeKnowledgeDisplayProfile(
            metadata.approved_profile ||
                metadata.approvedProfile ||
                metadata.knowledge_display_profile ||
                metadata.knowledgeDisplayProfile,
            normalizeKnowledgeFacts(metadata.knowledge_facts || metadata.knowledgeFacts) || undefined
        ),
        knowledgePages: normalizeKnowledgePages(metadata.knowledge_pages || metadata.knowledgePages),
        qualificationFields,
        recommendationTarget: String(metadata.recommendation_target || metadata.recommendationTarget || ''),
        recommendationContext: String(metadata.recommendation_context || metadata.recommendationContext || ''),
        followUps,
        stats,
        hasLaunched: Boolean(manager.has_launched) || status === 'running' || status === 'paused',
        llmModel: String(manager.llm_model || 'gpt-4o-mini'),
        llmTemperature: Number(manager.llm_temperature ?? 0.4),
        llmTopP: Number(manager.llm_top_p ?? 1),
        llmMaxOutputTokens: Number(manager.llm_max_output_tokens || 420),
        maxMessagesPerDialog: Number(manager.max_messages_per_dialog || 8),
        lastStatusError: manager.last_status_error,
        metadata,
        createdAt: manager.created_at || '',
        updatedAt: manager.updated_at || '',
    };
}

function normalizeConversation(conversation: BackendConversation): AIManagerConversation {
    return {
        id: conversation.id,
        operatorOverrideActive:
            conversation.operator_override === null
                ? false
                : typeof conversation.operator_override?.active === 'boolean'
                ? conversation.operator_override.active
                : null,
        status: conversation.status || '',
        salesStage: conversation.sales_stage || 'new_contact',
        salesStageTitle: conversation.sales_stage_title || 'Новый контакт',
        sourceMessage: conversation.source_message_snapshot || '',
        sourceChatTitle: conversation.source_chat_title_snapshot || conversation.lead_info?.chat_name,
        sourceAuthor: conversation.source_author_snapshot || conversation.lead_info?.author,
        latestMessagePreview: conversation.latest_message_preview || '',
        latestOutboundStatus: conversation.latest_outbound_status,
        messageCount: Number(conversation.message_count || 0),
        outboundCount: Number(conversation.outbound_count || 0),
        reviewPriority: conversation.review_priority,
        updatedAt: conversation.updated_at || '',
        createdAt: conversation.created_at || '',
        leadInfo: conversation.lead_info
            ? {
                  id: conversation.lead_info.id,
                  category: conversation.lead_info.category,
                  location: conversation.lead_info.location,
                  chatName: conversation.lead_info.chat_name,
                  author: conversation.lead_info.author,
                  messageDate: conversation.lead_info.message_date,
              }
            : undefined,
    };
}

function normalizeFieldProgress(value?: unknown): AIManagerFieldProgress | null {
    if (!value || typeof value !== 'object') {
        return null;
    }
    const source = value as Record<string, unknown>;
    const fields = Array.isArray(source.fields)
        ? source.fields
              .map((field, index) => {
                  const raw = field && typeof field === 'object' ? (field as Record<string, unknown>) : {};
                  const label = String(raw.label || '').trim();
                  if (!label) {
                      return null;
                  }
                  return {
                      id: String(raw.id || `field-${index}`),
                      label,
                      required: raw.required !== false,
                      collected: raw.collected === true,
                      missing: raw.missing === true,
                      value: raw.value,
                  };
              })
              .filter(Boolean)
        : [];

    return {
        fields: fields as AIManagerFieldProgress['fields'],
        requiredCount: Number(source.required_count || source.requiredCount || 0),
        collectedRequiredCount: Number(source.collected_required_count || source.collectedRequiredCount || 0),
        complete: source.complete === true,
    };
}

function normalizeTestDialogResult(data: BackendTestDialogResponse): AIManagerTestDialogResult {
    const assistantMessage = data.assistant_message || {};
    const assistantContent = String(assistantMessage.content || data.reply_draft || '').trim();

    return {
        assistantMessage: {
            id: assistantMessage.id,
            role: 'assistant',
            content: assistantContent || 'Ответ не сформирован.',
        },
        replyDraft: String(data.reply_draft || assistantContent || '').trim(),
        sessionId: data.session?.id || null,
        sessionState: {
            currentStageKey: data.session_state?.current_stage_key || null,
            lifecycleStatus: data.session_state?.lifecycle_status || null,
            salesStage: data.session_state?.sales_stage || null,
            salesOutcome: data.session_state?.sales_outcome || null,
            knownFacts: data.session_state?.known_facts || {},
        },
        reasoningSummary: String(data.reasoning_summary || '').trim(),
        missingFacts: Array.isArray(data.missing_facts)
            ? data.missing_facts.map((item) => String(item || '').trim()).filter(Boolean)
            : [],
        fieldProgress: normalizeFieldProgress(data.field_progress),
        suggestedSalesStage: String(data.suggested_sales_stage || '').trim(),
        usage: data.usage
            ? {
                  inputTokens: Number(data.usage.input_tokens || 0),
                  outputTokens: Number(data.usage.output_tokens || 0),
              }
            : undefined,
    };
}

function normalizeKnowledgeDocument(document: BackendKnowledgeDocument): AIManagerKnowledgeDocument {
    return {
        id: Number(document.id),
        agentProfile: String(document.agent_profile || ''),
        title: String(document.title || ''),
        summary: String(document.summary || ''),
        body: String(document.body || ''),
        scope: String(document.scope || ''),
        documentType: String(document.document_type || ''),
        priority: Number(document.priority || 0),
        sourceUrl: String(document.source_url || ''),
        tags: normalizeRules(document.tags),
        metadata: document.metadata || {},
        isActive: document.is_active !== false,
        createdAt: document.created_at || '',
        updatedAt: document.updated_at || '',
    };
}

function normalizeInstructionTemplate(template: BackendInstructionTemplate): AIManagerInstructionTemplate {
    return {
        id: Number(template.id),
        slug: String(template.slug || ''),
        name: String(template.name || ''),
        description: String(template.description || ''),
        dataCollectionSchema: Array.isArray(template.data_collection_schema) ? template.data_collection_schema : [],
        metadata: template.metadata || {},
    };
}

function normalizeTelegramAuthSession(session: BackendTelegramAuthSession): AIManagerTelegramAuthSession {
    return {
        id: Number(session.id),
        sessionToken: String(session.session_token || ''),
        phoneNumber: String(session.phone_number || ''),
        status: session.status || 'code_requested',
        lastError: String(session.last_error || ''),
    };
}

function unwrapList<T>(data: T[] | { results?: T[] }): T[] {
    if (Array.isArray(data)) {
        return data;
    }

    if (data && Array.isArray(data.results)) {
        return data.results;
    }

    return [];
}

const LEGACY_AI_MANAGER_STORAGE_KEYS = [
    'leadsmania_ai_managers',
    'leadsmania2_ai_managers',
    'lm2_ai_managers',
    'ai_managers',
];
const AI_MANAGER_KNOWLEDGE_ANALYSIS_TIMEOUT_MS = 45000;
const AI_MANAGER_TEST_DIALOG_TIMEOUT_MS = 45000;
const SERVER_MANAGED_PROFILE_METADATA_KEYS = [
    'owner_lead_pilot',
    'setup_test_server_evidence',
    'has_launched',
    'launched_at',
] as const;

function stripServerManagedProfileMetadata(metadata: Record<string, unknown>): Record<string, unknown> {
    const sanitized = { ...metadata };
    SERVER_MANAGED_PROFILE_METADATA_KEYS.forEach((key) => delete sanitized[key]);
    return sanitized;
}

function clearLegacyAIManagerStorage() {
    if (typeof window === 'undefined') {
        return;
    }

    LEGACY_AI_MANAGER_STORAGE_KEYS.forEach((key) => {
        window.localStorage.removeItem(key);
    });
}

export function shouldShowAIManagerInMenu(manager: AIManager) {
    const hasLeadStreams = manager.leadStreamIds.length > 0 || manager.leadStreams.length > 0;
    const wasLaunched = manager.hasLaunched || manager.status === 'running' || manager.status === 'paused';
    const setupCompleted =
        manager.status === 'ready_to_launch' ||
        manager.metadata.setup_completed === true ||
        manager.metadata.setupCompleted === true;

    return hasLeadStreams && (wasLaunched || setupCompleted);
}

class AIManagersService {
    private cachedManagers: AIManager[] = [];

    getManagers = async (): Promise<AIManager[]> => {
        clearLegacyAIManagerStorage();
        const { data } = await globalAgent.get<BackendAIManager[] | { results?: BackendAIManager[] }>(
            '/agent/profiles/'
        );
        const managers = unwrapList(data).map(normalizeManager);
        this.cachedManagers = managers;
        return managers;
    };

    getManagersSync = (): AIManager[] => {
        return this.cachedManagers;
    };

    getManager = async (id: string): Promise<AIManager> => {
        const { data } = await globalAgent.get<BackendAIManager>(`/agent/profiles/${id}/`);
        const manager = normalizeManager(data);
        this.cachedManagers = [manager, ...this.cachedManagers.filter((item) => item.id !== manager.id)];
        return manager;
    };

    analyzeKnowledgeWebsite = async ({
        url,
        offerNotes,
    }: {
        url: string;
        offerNotes?: string;
    }): Promise<AIManagerKnowledgeAnalysis> => {
        const { data } = await globalAgent.post<BackendWebsiteKnowledgeAnalysis>(
            '/agent/knowledge/analyze-website/',
            {
                url,
                offer_notes: offerNotes || '',
            },
            { timeout: AI_MANAGER_KNOWLEDGE_ANALYSIS_TIMEOUT_MS }
        );

        return normalizeKnowledgeAnalysis(data);
    };

    createManager = async (draft: NewAIManagerDraft): Promise<AIManager> => {
        const openingMessage = draft.firstMessage || buildFirstMessage(draft);
        const qualificationFields = normalizeQualificationFields(draft.qualificationFields);
        const followUps = normalizeFollowUps(draft.followUps);
        const firstFollowUp = followUps[0];
        const { data } = await globalAgent.post<BackendAIManager>('/agent/profiles/', {
            name: draft.name.trim() || 'Новый AI-менеджер',
            description: draft.goalDescription.trim(),
            is_active: draft.status === 'running',
            lead_stream_ids: draft.leadStreamIds,
            status: draft.status || (draft.metadata?.setup_version === 2 ? 'draft' : undefined),
            goal: draft.goal,
            goal_description: draft.goalDescription.trim(),
            flow_steps: normalizeRules(draft.flowSteps),
            data_to_collect:
                draft.goal === 'qualify'
                    ? qualificationFields.map((field) => field.label)
                    : normalizeRules(draft.dataToCollect),
            opening_message: openingMessage,
            handoff_rule: draft.handoffRule.trim(),
            stop_phrases: normalizeRules(draft.stopPhrases),
            avatar_palette_id: draft.avatarPaletteId,
            llm_model: draft.llmModel || 'gpt-4o-mini',
            llm_temperature: draft.llmTemperature ?? 0.4,
            llm_top_p: draft.llmTopP ?? 1,
            llm_max_output_tokens: draft.llmMaxOutputTokens ?? 420,
            max_messages_per_dialog: draft.maxMessagesPerDialog ?? 8,
            no_reply_followup_enabled: Boolean(firstFollowUp?.message),
            no_reply_followup_delay_hours: firstFollowUp?.delayHours || 24,
            no_reply_followup_text: firstFollowUp?.message || '',
            metadata: stripServerManagedProfileMetadata({
                source: 'leadsmania2',
                goal: draft.goal,
                offer_notes: String(draft.offerNotes || '').trim(),
                knowledge_source: String(draft.knowledgeSource || '').trim(),
                knowledge_source_url:
                    normalizeBackendSourceUrl(draft.knowledgeSourceUrl || draft.knowledgeSource) || '',
                knowledge_summary: String(draft.knowledgeSummary || '').trim(),
                knowledge_body: String(draft.knowledgeSummary || '').trim(),
                knowledge_facts: draft.knowledgeFacts || null,
                knowledge_pages: draft.knowledgePages || [],
                qualification_fields: qualificationFields,
                recommendation_target: String(draft.recommendationTarget || '').trim(),
                recommendation_context: String(draft.recommendationContext || '').trim(),
                follow_up_rules: followUps,
                approved_profile: draft.knowledgeDisplayProfile || null,
                knowledge_display_profile: draft.knowledgeDisplayProfile || null,
                ...(draft.metadata || {}),
            }),
        });
        const manager = normalizeManager(data);
        this.cachedManagers = [manager, ...this.cachedManagers.filter((item) => item.id !== manager.id)];
        return manager;
    };

    updateManager = async (id: string, patch: Partial<AIManager>): Promise<AIManager> => {
        if (patch.status && Object.keys(patch).length === 1) {
            return this.updateManagerStatus(id, patch.status);
        }

        const payload: Record<string, unknown> = {};
        let metadataPatch = patch.metadata ? { ...patch.metadata } : undefined;
        if (patch.name !== undefined) payload.name = patch.name;
        if (patch.goal !== undefined) payload.goal = patch.goal;
        if (patch.goalDescription !== undefined) payload.goal_description = patch.goalDescription;
        if (patch.flowSteps !== undefined) payload.flow_steps = patch.flowSteps;
        if (patch.dataToCollect !== undefined) payload.data_to_collect = patch.dataToCollect;
        if (patch.firstMessage !== undefined) payload.opening_message = patch.firstMessage;
        if (patch.handoffRule !== undefined) payload.handoff_rule = patch.handoffRule;
        if (patch.stopPhrases !== undefined) payload.stop_phrases = patch.stopPhrases;
        if (patch.avatarPaletteId !== undefined) payload.avatar_palette_id = patch.avatarPaletteId;
        if (patch.leadStreamIds !== undefined) payload.lead_stream_ids = patch.leadStreamIds;
        if (patch.llmModel !== undefined) payload.llm_model = patch.llmModel;
        if (patch.llmTemperature !== undefined) payload.llm_temperature = patch.llmTemperature;
        if (patch.llmTopP !== undefined) payload.llm_top_p = patch.llmTopP;
        if (patch.llmMaxOutputTokens !== undefined) payload.llm_max_output_tokens = patch.llmMaxOutputTokens;
        if (patch.maxMessagesPerDialog !== undefined) payload.max_messages_per_dialog = patch.maxMessagesPerDialog;
        if (patch.followUps !== undefined) {
            const followUps = normalizeFollowUps(patch.followUps);
            const firstFollowUp = followUps[0];
            payload.no_reply_followup_enabled = Boolean(firstFollowUp?.message);
            payload.no_reply_followup_delay_hours = firstFollowUp?.delayHours || 24;
            payload.no_reply_followup_text = firstFollowUp?.message || '';
            metadataPatch = {
                ...(metadataPatch || {}),
                follow_up_rules: followUps,
            };
        }
        if (patch.status !== undefined) {
            payload.status = patch.status;
            payload.is_active = patch.status === 'running';
        }
        if (metadataPatch !== undefined) {
            const current = this.cachedManagers.find((manager) => manager.id === id);
            payload.metadata = stripServerManagedProfileMetadata({
                ...(current?.metadata || {}),
                ...metadataPatch,
            });
        }

        const { data } = await globalAgent.patch<BackendAIManager>(`/agent/profiles/${id}/`, payload);
        const updated = normalizeManager(data);
        this.cachedManagers = this.cachedManagers.map((manager) => (manager.id === updated.id ? updated : manager));
        return updated;
    };

    deleteManager = async (id: string): Promise<void> => {
        await globalAgent.delete(`/agent/profiles/${id}/`);
        this.cachedManagers = this.cachedManagers.filter((manager) => manager.id !== id);
    };

    updateManagerStatus = async (id: string, status: AIManagerStatus): Promise<AIManager> => {
        try {
            const { data } = await globalAgent.patch<BackendAIManager | { manager: BackendAIManager }>(
                `/agent/profiles/${id}/status/`,
                { status }
            );
            const managerData = 'manager' in data ? data.manager : data;
            const updated = normalizeManager(managerData);
            this.cachedManagers = this.cachedManagers.map((manager) => (manager.id === updated.id ? updated : manager));
            return updated;
        } catch (error) {
            const managerData = (error as { response?: { data?: { manager?: BackendAIManager } } })?.response?.data
                ?.manager;

            if (managerData) {
                const updated = normalizeManager(managerData);
                this.cachedManagers = this.cachedManagers.map((manager) =>
                    manager.id === updated.id ? updated : manager
                );
                (error as { manager?: AIManager }).manager = updated;
            }

            throw error;
        }
    };

    getReviewQueue = async (managerId: string): Promise<AIManagerConversation[]> => {
        const { data } = await globalAgent.get<BackendConversation[]>(`/agent/profiles/${managerId}/conversations/`, {
            params: { status: 'human_review_required' },
        });
        if (!Array.isArray(data)) throw new Error('Неверный ответ очереди диалогов');
        return data.map(normalizeConversation).filter((item) => item.status === 'human_review_required');
    };

    getConversationTranscript = async (
        managerId: string,
        conversationId: number
    ): Promise<AIManagerConversationTranscript> => {
        if (!/^\d+$/.test(managerId) || !Number.isSafeInteger(conversationId) || conversationId <= 0) {
            throw new Error('Неверный адрес диалога');
        }
        const { data } = await globalAgent.get<{
            conversation?: BackendConversation & { agent_profile?: number | string };
            timeline?: unknown;
        }>(`/agent/conversations/${conversationId}/messages/`, { params: { agent_profile: managerId } });
        // The server enforces ownership. Check the requested scope again before displaying anything.
        if (data?.conversation?.id !== conversationId || String(data?.conversation?.agent_profile) !== managerId) {
            throw new Error('Диалог не принадлежит выбранному менеджеру');
        }
        const raw = data.conversation;
        const textValues = [
            raw.status,
            raw.sales_stage,
            raw.sales_stage_title,
            raw.source_message_snapshot,
            raw.source_chat_title_snapshot,
            raw.source_author_snapshot,
            raw.latest_message_preview,
            raw.lead_info?.author,
            raw.lead_info?.chat_name,
        ];
        if (textValues.some((value) => value != null && typeof value !== 'string')) {
            throw new Error('Неверный ответ истории диалога');
        }
        const result = {
            conversation: normalizeConversation(data.conversation),
            timeline: normalizeConversationTimeline(data.timeline),
            context: normalizeConversationContext(data.conversation),
        };
        if (isHumanControlled(result.conversation)) uncertainHandoffs.delete(`${managerId}:${conversationId}`);
        return result;
    };

    handoffNeedsVerification = (managerId: string, conversationId: number) =>
        uncertainHandoffs.has(`${managerId}:${conversationId}`) ||
        pendingHandoffs.has(`${managerId}:${conversationId}`);

    handoffConversation = (
        managerId: string,
        conversationId: number,
        note: string
    ): Promise<AIManagerHandoffResult> => {
        const key = `${managerId}:${conversationId}`;
        const pending = pendingHandoffs.get(key);
        if (pending) return pending;
        const request = (async () => {
            let current: AIManagerConversationTranscript;
            try {
                current = await this.getConversationTranscript(managerId, conversationId);
            } catch (error) {
                const status = (error as { response?: { status?: number } })?.response?.status;
                if (status === 401 || status === 403 || status === 404)
                    throw new ConversationHandoffError('unavailable');
                throw new ConversationHandoffError('preflight');
            }
            if (isHumanControlled(current.conversation)) {
                uncertainHandoffs.delete(key);
                return current;
            }
            if (uncertainHandoffs.has(key)) throw new ConversationHandoffError('uncertain');
            if (!canHandoff(current.conversation)) throw new ConversationHandoffError('changed');
            try {
                const { data } = await globalAgent.post<BackendConversation & { agent_profile?: number | string }>(
                    `/agent/conversations/${conversationId}/handoff/`,
                    { note: note.trim() },
                    { params: { agent_profile: managerId } }
                );
                if (
                    data?.id !== conversationId ||
                    String(data?.agent_profile) !== managerId ||
                    !isHumanControlled(normalizeConversation(data))
                ) {
                    throw new ConversationHandoffError('uncertain');
                }
                // Keep only the confirmed control state; refresh retrieves the complete conversation and timeline.
                uncertainHandoffs.delete(key);
                return {
                    conversation: { ...current.conversation, status: 'human_handoff', operatorOverrideActive: true },
                    context: normalizeConversationContext(data),
                };
            } catch (error) {
                const status = (error as { response?: { status?: number } })?.response?.status;
                if (status === 401 || status === 403 || status === 404 || status === 405) {
                    throw new ConversationHandoffError('unavailable');
                }
                if (status === 400 || status === 409) throw new ConversationHandoffError('rejected');
                // No automatic write retry: a timeout/5xx may arrive after the server changed the conversation.
                uncertainHandoffs.add(key);
                throw new ConversationHandoffError('uncertain');
            }
        })();
        pendingHandoffs.set(key, request);
        void request.finally(() => pendingHandoffs.delete(key)).catch(() => undefined);
        return request;
    };

    getKanban = async (managerId: string, limitPerStage?: number): Promise<AIManagerKanban> => {
        const { data } = await globalAgent.get<{
            agent_profile: number | string;
            agent_profile_status?: AIManagerStatus;
            total?: number;
            columns?: Array<{
                key: AIManagerFunnelStageKey;
                title: string;
                count: number;
                items: BackendConversation[];
            }>;
        }>(`/agent/profiles/${managerId}/kanban/`, {
            params: limitPerStage ? { limit_per_stage: limitPerStage } : undefined,
        });

        return {
            agentProfile: String(data.agent_profile),
            agentProfileStatus: data.agent_profile_status,
            total: Number(data.total || 0),
            columns: (data.columns || []).map((column) => ({
                key: column.key,
                title: column.title,
                count: Number(column.count || 0),
                items: (column.items || []).map(normalizeConversation),
            })),
        };
    };

    moveConversationStage = async (
        conversationId: number,
        salesStage: AIManagerFunnelStageKey
    ): Promise<AIManagerConversation> => {
        const { data } = await globalAgent.post<BackendConversation>(
            `/agent/conversations/${conversationId}/move-stage/`,
            {
                sales_stage: salesStage,
            }
        );
        return normalizeConversation(data);
    };

    testDialog = async ({
        managerId,
        messages,
        sessionId,
        sessionState,
    }: {
        managerId: string;
        messages: AIManagerTestDialogMessage[];
        sessionId?: number | null;
        sessionState?: AIManagerTestDialogSessionState | null;
    }): Promise<AIManagerTestDialogResult> => {
        const { data } = await globalAgent.post<BackendTestDialogResponse>(
            `/agent/profiles/${managerId}/test-dialog/`,
            {
                session_id: sessionId || null,
                messages: messages
                    .map((message) => ({
                        role: message.role === 'user' ? 'user' : 'assistant',
                        content: message.content,
                    }))
                    .filter((message) => message.content.trim()),
                known_facts: sessionState?.knownFacts || {},
                current_stage_key: sessionState?.currentStageKey || null,
                lifecycle_status: sessionState?.lifecycleStatus || null,
                sales_stage: sessionState?.salesStage || null,
                sales_outcome: sessionState?.salesOutcome || null,
            },
            { timeout: AI_MANAGER_TEST_DIALOG_TIMEOUT_MS }
        );

        return normalizeTestDialogResult(data);
    };

    acceptSetupTest = async ({
        managerId,
        sessionId,
        rating,
    }: {
        managerId: string;
        sessionId: number;
        rating: number;
    }): Promise<void> => {
        await globalAgent.post(`/agent/profiles/${managerId}/setup-test-review/`, {
            session_id: sessionId,
            rating,
        });
    };

    getInstructionTemplates = async (): Promise<AIManagerInstructionTemplate[]> => {
        const { data } = await globalAgent.get<
            BackendInstructionTemplate[] | { results?: BackendInstructionTemplate[] }
        >('/agent/instruction-templates/');
        return unwrapList(data).map(normalizeInstructionTemplate);
    };

    listKnowledgeDocuments = async (managerId: string): Promise<AIManagerKnowledgeDocument[]> => {
        const { data } = await globalAgent.get<BackendKnowledgeDocument[] | { results?: BackendKnowledgeDocument[] }>(
            '/agent/knowledge-documents/',
            {
                params: { agent_profile: managerId },
            }
        );
        return readKnowledgeDocumentList<BackendKnowledgeDocument>(data).map(normalizeKnowledgeDocument);
    };

    listAllKnowledgeDocuments = async (): Promise<AIManagerKnowledgeDocument[]> => {
        const { data } = await globalAgent.get<BackendKnowledgeDocument[] | { results?: BackendKnowledgeDocument[] }>(
            '/agent/knowledge-documents/'
        );
        return readKnowledgeDocumentList<BackendKnowledgeDocument>(data).map(normalizeKnowledgeDocument);
    };

    createKnowledgeDocument = async (
        payload: Partial<AIManagerKnowledgeDocument> & { agentProfile: string; title: string; body: string }
    ): Promise<AIManagerKnowledgeDocument> => {
        const { data } = await globalAgent.post<BackendKnowledgeDocument>('/agent/knowledge-documents/', {
            agent_profile: Number(payload.agentProfile),
            title: payload.title,
            summary: payload.summary || '',
            body: payload.body,
            scope: payload.scope || 'profile',
            document_type: payload.documentType || 'company_profile',
            priority: payload.priority ?? 80,
            source_url: normalizeBackendSourceUrl(payload.sourceUrl),
            tags: payload.tags || ['leadsmania2', 'website', 'offer', 'setup-v2'],
            metadata: payload.metadata || {},
            is_active: payload.isActive !== false,
        });
        return normalizeKnowledgeDocument(data);
    };

    updateKnowledgeDocument = async (
        id: number,
        payload: Partial<AIManagerKnowledgeDocument>
    ): Promise<AIManagerKnowledgeDocument> => {
        const { data } = await globalAgent.patch<BackendKnowledgeDocument>(`/agent/knowledge-documents/${id}/`, {
            ...(payload.title !== undefined ? { title: payload.title } : {}),
            ...(payload.summary !== undefined ? { summary: payload.summary } : {}),
            ...(payload.body !== undefined ? { body: payload.body } : {}),
            ...(payload.scope !== undefined ? { scope: payload.scope } : {}),
            ...(payload.documentType !== undefined ? { document_type: payload.documentType } : {}),
            ...(payload.priority !== undefined ? { priority: payload.priority } : {}),
            ...(payload.sourceUrl !== undefined ? { source_url: normalizeBackendSourceUrl(payload.sourceUrl) } : {}),
            ...(payload.tags !== undefined ? { tags: payload.tags } : {}),
            ...(payload.metadata !== undefined ? { metadata: payload.metadata } : {}),
            ...(payload.isActive !== undefined ? { is_active: payload.isActive } : {}),
        });
        return normalizeKnowledgeDocument(data);
    };

    deleteKnowledgeDocument = async (id: number): Promise<void> => {
        await globalAgent.delete(`/agent/knowledge-documents/${id}/`);
    };

    startTelegramAuth = async (managerId: string, phoneNumber: string): Promise<AIManagerTelegramAuthSession> => {
        const { data } = await globalAgent.post<BackendTelegramAuthSession>('/agent/telegram-auth/start/', {
            phone_number: phoneNumber,
            agent_profile_id: Number(managerId),
        });

        return normalizeTelegramAuthSession(data);
    };

    verifyTelegramAuth = async ({
        sessionToken,
        code,
        password,
    }: {
        sessionToken: string;
        code?: string;
        password?: string;
    }): Promise<AIManagerTelegramAuthSession> => {
        const { data } = await globalAgent.post<BackendTelegramAuthSession>('/agent/telegram-auth/verify/', {
            session_token: sessionToken,
            code: code || '',
            password: password || '',
        });

        return normalizeTelegramAuthSession(data);
    };
}

export default new AIManagersService();
